'use client';

import React from 'react';
import { BuildingCard, type BuildingCardProps } from './BuildingCard';
import type { Building } from '@repo/api';
import { buildingUrl } from '@/utils/routes';
import { ResponsiveLink } from '@/components/shared/ResponsiveLink';

export interface ClickableBuildingCardProps extends Omit<BuildingCardProps, 'building'> {
  /** Core building data from API */
  building: Building;
  /** Optional custom href. If not provided, will be constructed from building data */
  href?: string;
  /** Allow analytics data attributes (e.g. data-analytics-event) to be forwarded to the anchor */
  [key: `data-${string}`]: string | undefined;
}

/**
 * A clickable wrapper around BuildingCard that handles navigation to building detail pages.
 * Constructs the building URL automatically if href is not provided.
 */
export function ClickableBuildingCard({
  building,
  href,
  className,
  ...rest
}: ClickableBuildingCardProps) {
  const resolvedHref = href ?? buildingUrl(building);

  // Split rest into data-* attrs (the analytics attrs, which must land on the
  // clickable anchor so the global click listener can read them) and the
  // remaining visual props, which belong to <BuildingCard>.
  const dataAttrs: Record<string, string | undefined> = {};
  const cardProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (key.startsWith('data-')) {
      dataAttrs[key] = value as string | undefined;
    } else {
      cardProps[key] = value;
    }
  }

  return (
    <ResponsiveLink className={`w-full ${className || ''}`} href={resolvedHref} {...dataAttrs}>
      <BuildingCard building={building} {...(cardProps as Omit<BuildingCardProps, 'building'>)} />
    </ResponsiveLink>
  );
}
